﻿// packages/shared/src/services/firebaseService.ts
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getAuth, Auth } from 'firebase/auth';
import { getFunctions, Functions } from 'firebase/functions';
import { firebaseConfig, useEmulator, firebaseProjectConfig } from '../config/firebase';

export class FirebaseService {
  private static instance: FirebaseService;
  private app: FirebaseApp | null = null;
  private firestore: Firestore | null = null;
  private auth: Auth | null = null;
  private functions: Functions | null = null;
  private initialized: boolean = false;

  private constructor() {}

  public static getInstance(): FirebaseService {
    if (!FirebaseService.instance) {
      FirebaseService.instance = new FirebaseService();
    }
    return FirebaseService.instance;
  }

  public async initializeApp(): Promise<void> {
    // If this instance is already initialized, return early
    if (this.initialized) return;

    try {
      // Get existing app if available or create new one
      // Handle cases where getApps() might return null or undefined
      const existingApps = getApps() || [];
      this.app = existingApps.length === 0 
        ? initializeApp(firebaseConfig) 
        : existingApps[0];
      
      // Always get Firestore/Auth/Functions instances
      this.firestore = getFirestore(this.app);
      this.auth = getAuth(this.app);
      this.functions = getFunctions(this.app);
      
      // Connect to emulators if needed
      if (useEmulator) {
        try {
          const { connectFirestoreEmulator } = await import('firebase/firestore');
          const { connectAuthEmulator } = await import('firebase/auth');
          const { connectFunctionsEmulator } = await import('firebase/functions');
          
          connectFirestoreEmulator(this.firestore, 'localhost', firebaseProjectConfig.emulatorPorts.firestore);
          connectAuthEmulator(this.auth, `http://localhost:${firebaseProjectConfig.emulatorPorts.auth}`);
          connectFunctionsEmulator(this.functions, 'localhost', firebaseProjectConfig.emulatorPorts.functions);
          
          console.log('Connected to Firebase emulators');
        } catch (emulatorError) {
          console.warn('Failed to connect to emulators:', emulatorError);
        }
      }
      
      this.initialized = true;
      console.log('Firebase initialized successfully');
    } catch (error) {
      console.error('Error initializing Firebase:', error);
      throw error;
    }
  }

  public getFirestore(): Firestore {
    if (!this.firestore) {
      throw new Error('Firestore not initialized. Call initializeApp() first.');
    }
    return this.firestore;
  }

  public getAuth(): Auth {
    if (!this.auth) {
      throw new Error('Auth not initialized. Call initializeApp() first.');
    }
    return this.auth;
  }

  public getFunctions(): Functions {
    if (!this.functions) {
      throw new Error('Functions not initialized. Call initializeApp() first.');
    }
    return this.functions;
  }
}